import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import {
	json,
	type ActionFunctionArgs,
	type LoaderFunctionArgs,
	type MetaFunction,
} from '@remix-run/node'
import { Form, Link, useActionData, useSearchParams } from '@remix-run/react'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import {
	StatusButton,
	GeneralErrorBoundary,
	ErrorList,
	Field,
	Spacer,
	Label,
	Input,
	CheckboxField,
	H1,
} from '#app/ui/components'
import { login, requireAnonymous } from '#app/utils/auth.server.ts'
import { appName } from '#app/utils/constants.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { EmailSchema, PasswordSchema } from '#app/utils/user-validation.ts'
import { handleNewSession } from './login.server.ts'

const LoginFormSchema = z.object({
	email: EmailSchema,
	password: PasswordSchema,
	redirectTo: z.string().optional(),
	remember: z.boolean().optional(),
})

export async function loader({ request }: LoaderFunctionArgs) {
	await requireAnonymous(request)
	return json({})
}

export async function action({ request }: ActionFunctionArgs) {
	await requireAnonymous(request)
	const formData = await request.formData()
	checkHoneypot(formData)
	const submission = await parseWithZod(formData, {
		schema: (intent) =>
			LoginFormSchema.transform(async (data, ctx) => {
				if (intent !== null) return { ...data, session: null }

				const session = await login(data)
				if (!session) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: `Invalid email or password`,
					})
					return z.NEVER
				}

				return { ...data, session }
			}),
		async: true,
	})

	if (submission.status !== `success` || !submission.value.session) {
		return json(
			{ result: submission.reply({ hideFields: [`password`] }) },
			{ status: submission.status === `error` ? 401 : 200 },
		)
	}

	const { session, remember, redirectTo } = submission.value

	return handleNewSession({
		request,
		session,
		remember: remember ?? false,
		redirectTo,
	})
}

export default function LoginPage() {
	const actionData = useActionData<typeof action>()
	const isPending = useIsPending()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get(`redirectTo`)

	const [form, fields] = useForm({
		id: `login-form`,
		constraint: getZodConstraint(LoginFormSchema),
		defaultValue: { redirectTo },
		lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: LoginFormSchema })
		},
		shouldRevalidate: `onBlur`,
	})

	return (
		<div className="flex min-h-full flex-col justify-center pb-32 pt-20">
			<div className="mx-auto w-full max-w-xl">
				<div className="flex flex-col gap-3 text-center">
					<H1>Welcome back!</H1>
					<p className="text-body-md text-muted-600">
						Please enter your details.
					</p>
				</div>
				<Spacer size="xs" />

				<div>
					<div className="mx-auto w-full max-w-md px-8">
						<Form
							method="POST"
							{...getFormProps(form)}
							className="flex flex-col gap-6"
						>
							<HoneypotInputs />
							<Field>
								<Label htmlFor={fields.email.id}>Email</Label>
								<Input
									{...{
										...getInputProps(fields.email, { type: `text` }),
										autoFocus: true,
										className: `lowercase`,
										autoComplete: `email`,
									}}
								></Input>
								<ErrorList errors={fields.email.errors} />
							</Field>

							<Field>
								<Label htmlFor={fields.password.id}>Password</Label>
								<Input
									{...getInputProps(fields.password, { type: `password` })}
									autoComplete="current-password"
								/>
								<ErrorList errors={fields.password.errors} />
							</Field>

							<div className="flex justify-between">
								<CheckboxField
									labelProps={{
										htmlFor: fields.remember.id,
										children: `Remember me`,
									}}
									buttonProps={getInputProps(fields.remember, {
										type: `checkbox`,
									})}
									errors={fields.remember.errors}
								/>
								<div>
									<Link
										to="/forgot-password"
										className="text-body-xs font-semibold"
									>
										Forgot password?
									</Link>
								</div>
							</div>
							<input
								{...getInputProps(fields.redirectTo, { type: `hidden` })}
							/>
							<ErrorList errors={form.errors} id={form.errorId} />
							<div className="flex items-center justify-between gap-6">
								<StatusButton
									className="w-full"
									status={isPending ? `pending` : (form.status ?? `idle`)}
									type="submit"
									disabled={isPending}
								>
									Log in
								</StatusButton>
							</div>
						</Form>
						{/* <ul className="mt-5 flex flex-col gap-5 border-b-2 border-t-2 border-brand-border py-3">
						{providerNames.map((providerName) => (
							<li key={providerName}>
								<ProviderConnectionForm
								type="Login"
								providerName={providerName}
								redirectTo={redirectTo}
								/>
							</li>
						))}
						</ul> */}
						<div className="mt-6 flex items-center justify-center gap-2">
							<span className="text-muted-600">New here?</span>
							<Link
								to={
									redirectTo
										? `/signup?${encodeURIComponent(redirectTo)}`
										: `/signup`
								}
							>
								Create an account
							</Link>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

export const meta: MetaFunction = () => {
	return [{ title: `Login to ${appName}` }]
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
